import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import { message } from 'antd';

import { useAuthStore } from '@/stores/authStore';

export const apiBaseURL = '/api/v2';

export const api: AxiosInstance = axios.create({
  baseURL: apiBaseURL,
  timeout: 30_000,
  withCredentials: true, // 9.2c-2: send session cookie with all API requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// 防 redirect loop（module-level flag,5s reset）
let redirectingToLogin = false;

function navigateTo(path: string): void {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  setTimeout(() => {
    redirectingToLogin = false;
  }, 5000);
  // 非 React 上下文,用 window.location（Codex Minor Note 3）
  window.location.assign(path);
}

// /auth/* 检测:baseURL=/api/v2 时 config.url 可能是 /auth/login,也兼容 absolute（Codex Minor Note 2）
export function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return /\/auth\//.test(url);
}

// 8-row interceptor table（Codex Decision 5）。导出便于单测。
export function handleResponseError(error: {
  response?: { status?: number; data?: { code?: string; detail?: string } };
  config?: { url?: string };
  message?: string;
}): Promise<never> {
  const status = error.response?.status;
  const code = error.response?.data?.code;
  const detail = error.response?.data?.detail ?? error.message;
  const isAuth = isAuthEndpoint(error.config?.url);

  if (status === 401) {
    if (isAuth) {
      if (/\/auth\/me/.test(error.config?.url ?? '')) {
        // row 2: 401 /auth/me（bootstrap）→ clear store,**不** redirect(RequireAuth catch 自处理,避免 double redirect)
        useAuthStore.getState().clearAuth();
      }
      // row 1: 401 /auth/login → caller(Login.tsx)显 error,不 clear 不 redirect
    } else {
      // row 3: 401 other protected → clear store + redirect /login?next=（一次）
      useAuthStore.getState().clearAuth();
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      navigateTo(`/login?next=${next}`);
    }
  } else if (status === 403) {
    if (code === 'PASSWORD_CHANGE_REQUIRED') {
      navigateTo('/account/change-password'); // row 4
    } else if (code === 'ACCOUNT_DISABLED') {
      // row 5: from login,caller 显错误,不 retry
    } else {
      message.error('权限不足'); // row 6
    }
  } else if (status === 404) {
    // row 7: 静默,caller fallback
  } else if (status && status >= 500) {
    message.error(`服务器错误：${detail}`); // row 8
  } else if (status && status >= 400) {
    message.error(detail);
  } else {
    message.error('网络异常，请检查连接');
  }
  return Promise.reject(error);
}

api.interceptors.response.use((response: AxiosResponse) => response, handleResponseError);

// Convenience health check
export async function healthCheck(): Promise<{ status: string }> {
  const { data } = await axios.get('/health');
  return data;
}
